import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiSave, FiMail, FiEye, FiCode, FiLayout, FiInfo } from 'react-icons/fi';
import { emailTemplatesAPI, settingsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useCountry } from '../../context/CountryContext';
import Modal from '../../components/Common/Modal';
import EmailBodyEditor, { isEmailBodyEmpty } from '../../components/Common/EmailBodyEditor';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import AuditTimeline from '../../components/Common/AuditTimeline';

function stripHtml(html) {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

/**
 * Detects "rich" HTML that ReactQuill cannot faithfully edit.
 *
 * ReactQuill only keeps a small whitelist of tags/formats (bold, italic,
 * lists, headers, color, link, image, align…). Tags like <!DOCTYPE>,
 * <html>, <head>, <style>, <table>, <tr>, <td> are silently dropped the
 * moment the Quill editor mounts with that HTML. If we let Quill mount
 * with rich HTML, its first onChange fires a stripped-down version which
 * overwrites the user's source code — exactly the bug described
 * ("after I switch to Design the HTML disappears").
 *
 * This helper is used to SKIP mounting Quill when the stored body is rich
 * HTML, preserving the source code.
 */
function isRichEmailHtml(html) {
  if (!html || typeof html !== 'string') return false;
  const s = html.toLowerCase();
  if (/<!doctype/.test(s)) return true;
  if (/<html[\s>]/.test(s)) return true;
  if (/<head[\s>]/.test(s)) return true;
  if (/<body[\s>]/.test(s)) return true;
  if (/<style[\s>]/.test(s)) return true;
  if (/<table[\s>]/.test(s)) return true;
  if (/<tr[\s>]/.test(s)) return true;
  if (/<td[\s>]/.test(s)) return true;
  const styleHits = (s.match(/style\s*=/g) || []).length;
  if (styleHits >= 3) return true;
  return false;
}

const EMPTY_FORM = {
  name: '',
  subject_template: '',
  body_template: '',
  is_active: 1,
  is_global: 0
};
const MERGE_TAGS = [
  // Recommended common fields
  { key: 'student_name', label: 'Student Name' },
  { key: 'agency_name', label: 'Agency Name' },
  { key: 'student_ref', label: 'Student Ref' },
  { key: 'agency_ref', label: 'Agency Ref' },
  { key: 'student_email', label: 'Student Email' },
  { key: 'agency_email', label: 'Agency Email' },
  { key: 'student_phone', label: 'Student Phone' },
  { key: 'agency_phone', label: 'Agency Phone' },
  { key: 'owner_name', label: 'Owner Name' },
  { key: 'owner_email', label: 'Owner Email' },
  { key: 'course', label: 'Course' },
  { key: 'intake', label: 'Intake' },
  { key: 'campus', label: 'Campus' },
  { key: 'today_date', label: 'Today Date' },

  // Student full details
  { key: 'student_id', label: 'Student ID' },
  { key: 'student_title', label: 'Student Title' },
  { key: 'student_first_name', label: 'Student First Name' },
  { key: 'student_last_name', label: 'Student Last Name' },
  { key: 'student_dob', label: 'Student DOB' },
  { key: 'student_address', label: 'Student Address' },
  { key: 'student_city', label: 'Student City' },
  { key: 'student_state', label: 'Student State' },
  { key: 'student_country', label: 'Student Country' },
  { key: 'student_zip_code', label: 'Student Zip Code' },
  { key: 'student_passport_no', label: 'Student Passport No' },
  { key: 'student_gender', label: 'Student Gender' },
  { key: 'student_language', label: 'Student Language' },
  { key: 'student_current_degree', label: 'Student Current Degree' },
  { key: 'student_type', label: 'Student Type' },
  { key: 'student_formation_type', label: 'Student Formation Type' },
  { key: 'student_status', label: 'Student Status' },
  { key: 'student_phone_country_code', label: 'Student Phone Country Code' },

  // Agency full details (no "lead" wording in UI)
  { key: 'agency_id', label: 'Agency ID' },
  { key: 'agency_source', label: 'Agency Source' },
  { key: 'agency_status', label: 'Agency Status' },
  { key: 'agency_contact_person', label: 'Agency Contact Person' },
  { key: 'agency_country', label: 'Agency Country' },
  { key: 'agency_state', label: 'Agency State' },
  { key: 'agency_city', label: 'Agency City' },
  { key: 'agency_remarks', label: 'Agency Remarks' },
  { key: 'agency_phone_country_code', label: 'Agency Phone Country Code' },
];

const EmailTemplateList = () => {
  const { t } = useTranslation();
  const { user, canPerformAction } = useAuth();
  const { activeCountry, getConfig } = useCountry();
  const countryConfig = getConfig();
  const isAdmin = ['super_admin', 'admin', 'manager'].includes(String(user?.role || '').toLowerCase());
  const isSuperAdmin = String(user?.role || '').toLowerCase() === 'super_admin';
  const canManageGlobalTemplates = ['super_admin', 'admin'].includes(String(user?.role || '').toLowerCase());
  // Authoring matches API: mailbox "create" permission (same rule for all countries / roles).
  const canAuthor = canPerformAction('mailbox', 'create');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedMergeTag, setSelectedMergeTag] = useState('student_name');
  // Body editor mode for the Add/Edit modal: 'design' | 'html' | 'preview'
  const [bodyMode, setBodyMode] = useState('design');
  // Per-row preview modal state (shown to everyone — including end users).
  const [previewItem, setPreviewItem] = useState(null);

  // Refs for cursor-aware merge-tag insertion in the Add/Edit modal.
  // - subjectRef: <textarea> for the subject (native selectionStart/End)
  // - htmlRef:    <textarea> used in the "HTML" tab (native selectionStart/End)
  // - quillRef:   ReactQuill instance (use .getEditor() for Quill API)
  const subjectRef = useRef(null);
  const htmlRef = useRef(null);
  const quillRef = useRef(null);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await emailTemplatesAPI.list();
      setTemplates(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || t('emailTemplates.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [activeCountry]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  useEffect(() => {
    const id = editingItem?.template_id;
    if (!showModal || !id) {
      setHistory([]);
      setHistoryLoading(false);
      return;
    }
    setHistoryLoading(true);
    settingsAPI
      .getAuditLog({ entity_type: 'email_template', entity_id: id, limit: 200, page: 1 })
      .then((r) => setHistory(r.data || []))
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
  }, [editingItem?.template_id, showModal]);

  const handleAdd = () => {
    if (!canAuthor) return;
    setEditingItem(null);
    setForm(EMPTY_FORM);
    setSelectedMergeTag('student_name');
    setBodyMode('design');
    setShowModal(true);
  };

  const handleEdit = (item) => {
    if (!canAuthor) return;
    setEditingItem(item);
    const body = item.body_template || '';
    setForm({
      name: item.name || '',
      subject_template: item.subject_template || '',
      body_template: body,
      is_active: item.is_active === 0 ? 0 : 1,
      is_global: Number(item.is_global) ? 1 : 0
    });
    setSelectedMergeTag('student_name');
    // If the template is rich HTML, open the HTML tab directly so the user
    // can edit the source without seeing the "Design editor disabled" guard.
    setBodyMode(isRichEmailHtml(body) ? 'html' : 'design');
    setShowModal(true);
  };

  const handlePreview = (item) => {
    setPreviewItem(item);
  };

  const mergeTagText = `{{${selectedMergeTag}}}`;

  /**
   * Insert `tag` at the current caret position inside a plain <textarea> / <input>.
   * Falls back to appending if we can't read the selection (e.g. ref not mounted).
   * After insert, restores focus and moves the caret to the end of the inserted text
   * so the user can keep typing naturally.
   */
  const insertAtTextareaCursor = (ref, currentValue, tag, applyValue) => {
    const el = ref.current;
    const value = currentValue || '';
    if (!el || typeof el.selectionStart !== 'number') {
      applyValue(`${value}${tag}`);
      return;
    }
    const start = el.selectionStart;
    const end = el.selectionEnd != null ? el.selectionEnd : start;
    const next = `${value.slice(0, start)}${tag}${value.slice(end)}`;
    applyValue(next);
    const caret = start + tag.length;
    // Wait for React to flush the new value, then restore focus + caret.
    requestAnimationFrame(() => {
      try {
        el.focus();
        el.setSelectionRange(caret, caret);
      } catch (_e) { /* noop */ }
    });
  };

  const insertTagInSubject = () => {
    insertAtTextareaCursor(subjectRef, form.subject_template, mergeTagText, (next) => {
      setForm((f) => ({ ...f, subject_template: next }));
    });
  };

  const insertTagInBody = () => {
    // HTML (raw source) tab → insert at the textarea caret.
    if (bodyMode === 'html') {
      insertAtTextareaCursor(htmlRef, form.body_template, mergeTagText, (next) => {
        setForm((f) => ({ ...f, body_template: next }));
      });
      return;
    }

    // Design (rich Quill) tab → use Quill's API so the tag lands exactly at
    // the current selection / caret, preserving existing formatting.
    if (bodyMode === 'design') {
      const instance = quillRef.current;
      const quill = instance && typeof instance.getEditor === 'function' ? instance.getEditor() : null;
      if (quill) {
        // `getSelection(true)` focuses the editor if needed and returns the
        // last selection range (or the end of the doc).
        const range = quill.getSelection(true) || { index: quill.getLength(), length: 0 };
        const index = range.index;
        if (range.length) {
          quill.deleteText(index, range.length, 'user');
        }
        quill.insertText(index, mergeTagText, 'user');
        quill.setSelection(index + mergeTagText.length, 0, 'user');
        return;
      }
      // Fallback if Quill isn't mounted yet.
      setForm((f) => ({ ...f, body_template: `${f.body_template || ''}${mergeTagText}` }));
      return;
    }

    // Preview tab has no caret → append as a new paragraph.
    setForm((f) => {
      const current = String(f.body_template || '').trim();
      if (!current) return { ...f, body_template: `<p>${mergeTagText}</p>` };
      return { ...f, body_template: `${current}<p>${mergeTagText}</p>` };
    });
  };

  const handleSubmit = async () => {
    if (!form.name || !form.subject_template || isEmailBodyEmpty(form.body_template)) {
      toast.error(t('emailTemplates.validationRequired'));
      return;
    }
    try {
      const payload = {
        name: String(form.name).trim(),
        subject_template: form.subject_template,
        body_template: form.body_template,
        is_active: form.is_active ? 1 : 0,
        // Only super admin/admin can set global templates; backend enforces as well
        is_global: canManageGlobalTemplates ? (form.is_global ? 1 : 0) : 0
      };
      if (editingItem?.template_id) {
        await emailTemplatesAPI.update(editingItem.template_id, payload);
        toast.success(t('emailTemplates.templateUpdated'));
      } else {
        await emailTemplatesAPI.create(payload);
        toast.success(t('emailTemplates.templateCreated'));
      }
      setShowModal(false);
      setEditingItem(null);
      setForm(EMPTY_FORM);
      loadTemplates();
    } catch (err) {
      toast.error(err.response?.data?.message || t('emailTemplates.saveFailed'));
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(t('emailTemplates.confirmDeactivate'))) return;
    try {
      await emailTemplatesAPI.delete(item.template_id);
      toast.success(t('emailTemplates.templateDeleted'));
      loadTemplates();
    } catch (err) {
      toast.error(err.response?.data?.message || t('emailTemplates.deleteFailed'));
    }
  };

  const rows = useMemo(() => templates, [templates]);

  return (
    <div>
      <div className="page-header">
        <div>
          <h2><FiMail style={{ marginRight: 8 }} />{t('emailTemplates.title')}</h2>
          <p className="page-subtitle">{countryConfig.flag} {t('emailTemplates.subtitle', { country: countryConfig.label })}</p>
        </div>
      </div>

      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <h3>{t('emailTemplates.templatesCount', { count: rows.length })}</h3>
        {canAuthor ? (
          <button className="btn btn-primary btn-sm" onClick={handleAdd}><FiPlus /> {t('emailTemplates.addBtn')}</button>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-muted)' }}>
            <FiInfo size={13} /> {t('emailTemplates.viewOnlyHint', { defaultValue: 'View only — your role needs mailbox create permission to add or edit templates.' })}
          </div>
        )}
      </div>

      {loading ? <div className="loading-spinner"><div className="spinner"></div></div> : (
        <div className="table-container">
          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>{t('common.name')}</th>
                  <th>{t('emailTemplates.scopeHeader')}</th>
                  {isAdmin && <th>{t('common.createdBy')}</th>}
                  <th>{t('emailTemplates.subjectTemplateHeader')}</th>
                  <th>{t('emailTemplates.bodyTemplateHeader')}</th>
                  <th>{t('common.status')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((item) => (
                  <tr key={item.template_id}>
                    <td style={{ fontWeight: 600 }}>{item.name || '-'}</td>
                    <td>
                      <span
                        className="badge"
                        style={{
                          background: Number(item.is_global) ? 'var(--purple-light)' : 'var(--bg-secondary)',
                          color: Number(item.is_global) ? 'var(--purple)' : 'var(--text-secondary)'
                        }}
                      >
                        {Number(item.is_global) ? t('emailTemplates.globalBadge') : t('emailTemplates.personalBadge')}
                      </span>
                    </td>
                    {isAdmin && <td>{item.created_by_name || '-'}</td>}
                    <td style={{ maxWidth: 320 }}>
                      <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={item.subject_template || ''}>
                        {item.subject_template || '-'}
                      </div>
                    </td>
                    <td style={{ maxWidth: 420 }}>
                      <div
                        style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        title={stripHtml(item.body_template || '') || item.body_template || ''}
                      >
                        {stripHtml(item.body_template || '') || '-'}
                      </div>
                    </td>
                    <td>
                      <span style={{
                        padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                        color: Number(item.is_active) ? 'var(--success)' : 'var(--danger)',
                        background: Number(item.is_active) ? 'var(--success-light)' : 'var(--danger-light)'
                      }}>
                        {Number(item.is_active) ? t('emailTemplates.activeBadge') : t('emailTemplates.inactiveBadge')}
                      </span>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button
                          className="edit"
                          onClick={() => handlePreview(item)}
                          title={t('emailTemplates.previewAction', { defaultValue: 'Preview' })}
                        >
                          <FiEye />
                        </button>
                        {canAuthor && (
                          <>
                            <button className="edit" onClick={() => handleEdit(item)} title={t('common.edit')}>
                              <FiEdit />
                            </button>
                            <button className="delete" onClick={() => handleDelete(item)} title={t('common.delete')}>
                              <FiTrash2 />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={isAdmin ? 7 : 6} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                      {t('emailTemplates.noTemplates')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setHistory([]);
          setHistoryLoading(false);
        }}
        title={editingItem ? t('emailTemplates.editTitle') : t('emailTemplates.addTitle')}
        size="lg"
        footer={<>
          <button className="btn btn-outline" onClick={() => setShowModal(false)}>{t('common.cancel')}</button>
          <button className="btn btn-primary" onClick={handleSubmit}><FiSave /> {editingItem ? t('common.update') : t('common.save')}</button>
        </>}
      >
        <div className="form-grid">
          <div className="form-group full-width">
            <label>{t('emailTemplates.templateNameLabel')}</label>
            <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </div>
          <div className="form-group full-width">
            <label>{t('emailTemplates.subjectTemplateLabel')}</label>
            <textarea
              ref={subjectRef}
              rows={3}
              value={form.subject_template}
              onChange={(e) => setForm((f) => ({ ...f, subject_template: e.target.value }))}
              placeholder={t('emailTemplates.subjectPlaceholder')}
            />
          </div>
          {/*
            NOTE: Intentionally NOT using `.form-group` here so the global stylesheet
            (`.form-group textarea { height: 100px; padding: 10px 12px; }`) doesn't
            squash the HTML source editor into a tiny, hard-to-edit box.
            We inline `gridColumn: '1 / -1'` because the parent `.form-grid` is a
            2-column grid; only `.form-group.full-width` gets the auto span via CSS.
          */}
          <div style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 10, width: '100%', minWidth: 0 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
              {t('emailTemplates.bodyTemplateLabel')}
            </label>

            {/* Merge-tag picker: framed pill row, not a bare select */}
            <div
              style={{
                display: 'flex',
                gap: 10,
                flexWrap: 'wrap',
                alignItems: 'center',
                padding: '10px 12px',
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border)',
                borderRadius: 10
              }}
            >
              <span style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                color: 'var(--text-muted)'
              }}>
                {t('emailTemplates.mergeTagLabel', { defaultValue: 'Merge tag' })}
              </span>
              <select
                value={selectedMergeTag}
                onChange={(e) => setSelectedMergeTag(e.target.value)}
                style={{
                  flex: '1 1 240px',
                  minWidth: 220,
                  maxWidth: 360,
                  height: 34,
                  padding: '0 10px',
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--text)',
                  background: 'var(--white)',
                  border: '1px solid var(--input-border, var(--border))',
                  borderRadius: 8,
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                {MERGE_TAGS.map((tag) => (
                  <option key={tag.key} value={tag.key}>
                    {tag.label}  •  {`{{${tag.key}}}`}
                  </option>
                ))}
              </select>
              <code style={{
                fontSize: 12,
                padding: '4px 8px',
                background: 'var(--white)',
                border: '1px dashed var(--border)',
                borderRadius: 6,
                color: 'var(--primary)',
                fontFamily: 'Menlo, Consolas, monospace'
              }}>
                {mergeTagText}
              </code>
              <div style={{ flex: 1 }} />
              {/*
                `onMouseDown` preventDefault keeps the caret/selection in the
                subject textarea / HTML textarea / Quill editor so the tag
                lands exactly where the user was typing.
              */}
              <button
                type="button"
                className="btn btn-sm btn-outline"
                onMouseDown={(e) => e.preventDefault()}
                onClick={insertTagInSubject}
              >
                {t('emailTemplates.insertInSubject', { defaultValue: 'Insert in Subject' })}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onMouseDown={(e) => e.preventDefault()}
                onClick={insertTagInBody}
              >
                {t('emailTemplates.insertInBody', { defaultValue: 'Insert in Body' })}
              </button>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {t('emailTemplates.mergeTagsHelp', { defaultValue: 'Pick a merge tag and insert it into the subject or body. Tags are replaced with real values when the template is sent.' })}
            </div>

            {/* Tabbed body editor: Design (rich) / HTML (raw source) / Preview (rendered iframe) */}
            <div
              role="tablist"
              style={{
                display: 'inline-flex',
                gap: 4,
                padding: 3,
                borderRadius: 8,
                background: 'var(--bg-secondary)',
                width: 'fit-content'
              }}
            >
              {[
                { id: 'design', icon: <FiLayout size={13} />, label: t('emailTemplates.tabDesign', { defaultValue: 'Design' }) },
                { id: 'html', icon: <FiCode size={13} />, label: t('emailTemplates.tabHtml', { defaultValue: 'HTML' }) },
                { id: 'preview', icon: <FiEye size={13} />, label: t('emailTemplates.tabPreview', { defaultValue: 'Preview' }) }
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={bodyMode === tab.id}
                  onClick={() => setBodyMode(tab.id)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 12px',
                    border: 'none',
                    borderRadius: 6,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    background: bodyMode === tab.id ? 'var(--white)' : 'transparent',
                    color: bodyMode === tab.id ? 'var(--primary)' : 'var(--text-muted)',
                    boxShadow: bodyMode === tab.id ? '0 1px 2px rgba(0,0,0,0.06)' : 'none'
                  }}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </div>

            {bodyMode === 'design' && (
              isRichEmailHtml(form.body_template) ? (
                /*
                  Guard: the stored body is full HTML (doctype / tables /
                  <style>…). If we mounted Quill here, it would strip all
                  of that the moment it loads and overwrite the source.
                  Instead, block the Design editor and give the user
                  deliberate choices: switch to HTML / Preview, or
                  explicitly "Convert to simple text" which clears the
                  rich source and opens Quill for plain authoring.
                */
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12,
                    padding: 16,
                    border: '1.5px dashed #fcd34d',
                    background: '#fffbeb',
                    color: '#78350f',
                    borderRadius: 10
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <FiInfo size={18} style={{ flexShrink: 0, marginTop: 2, color: '#b45309' }} />
                    <div style={{ fontSize: 13, lineHeight: 1.55 }}>
                      <strong style={{ fontSize: 13 }}>
                        {t('emailTemplates.designLockedTitle', { defaultValue: 'Design editor is disabled for this template' })}
                      </strong>
                      <div style={{ marginTop: 4 }}>
                        {t('emailTemplates.designLockedBody', {
                          defaultValue:
                            'This template uses full HTML (tables, <style>, doctype, etc.) that the simple Design editor cannot display without stripping. Your source is preserved — edit it in the HTML tab, or use Preview to see the final design.'
                        })}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={() => setBodyMode('html')}
                    >
                      <FiCode size={13} style={{ marginRight: 6 }} />
                      {t('emailTemplates.openHtmlEditor', { defaultValue: 'Open HTML editor' })}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline"
                      onClick={() => setBodyMode('preview')}
                    >
                      <FiEye size={13} style={{ marginRight: 6 }} />
                      {t('emailTemplates.viewPreview', { defaultValue: 'View preview' })}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline"
                      style={{ color: '#b91c1c', borderColor: '#fca5a5' }}
                      onClick={() => {
                        const ok = window.confirm(
                          t('emailTemplates.confirmSimplify', {
                            defaultValue:
                              'Switching to the Design editor will permanently strip the HTML source (tables, styles, doctype). Continue?'
                          })
                        );
                        if (!ok) return;
                        // Clear the rich source so Quill can mount safely
                        // on an empty body and the user can re-author in
                        // the simple editor.
                        setForm((f) => ({ ...f, body_template: '' }));
                      }}
                    >
                      {t('emailTemplates.convertToSimple', { defaultValue: 'Convert to simple editor (clears HTML)' })}
                    </button>
                  </div>
                </div>
              ) : (
                <EmailBodyEditor
                  ref={quillRef}
                  value={form.body_template}
                  onChange={(html) => setForm((f) => ({ ...f, body_template: html }))}
                  placeholder={t('emailTemplates.bodyPlaceholder')}
                  minHeight={260}
                />
              )
            )}

            {bodyMode === 'html' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {t('emailTemplates.htmlHelp', {
                    defaultValue: 'Paste full HTML for the email design (inline styles recommended). The Preview tab shows exactly what recipients will see.'
                  })}
                </div>
                <textarea
                  ref={htmlRef}
                  key="email-template-html-source"
                  value={form.body_template || ''}
                  onChange={(e) => setForm((f) => ({ ...f, body_template: e.target.value }))}
                  placeholder={'<table width="100%" style="font-family:Arial,sans-serif;...">\n  <tr><td>Hello {{student_name}},</td></tr>\n</table>'}
                  spellCheck={false}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  readOnly={false}
                  disabled={false}
                  rows={16}
                  style={{
                    display: 'block',
                    width: '100%',
                    height: 360,
                    minHeight: 320,
                    padding: 12,
                    fontFamily: '"Menlo", "Consolas", "Courier New", monospace',
                    fontSize: 13,
                    lineHeight: 1.55,
                    border: '1.5px solid var(--input-border, #e2e8f0)',
                    borderRadius: 8,
                    resize: 'vertical',
                    background: '#ffffff',
                    color: '#0f172a',
                    outline: 'none',
                    boxSizing: 'border-box',
                    whiteSpace: 'pre',
                    overflow: 'auto'
                  }}
                />
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  {t('emailTemplates.htmlChars', {
                    defaultValue: '{{count}} characters',
                    count: (form.body_template || '').length
                  })}
                </div>
              </div>
            )}

            {bodyMode === 'preview' && (
              <div
                style={{
                  width: '100%',
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  overflow: 'hidden',
                  background: '#fff'
                }}
              >
                <div style={{
                  padding: '8px 12px',
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                  color: 'var(--text-muted)',
                  background: 'var(--bg-secondary)',
                  borderBottom: '1px solid var(--border)'
                }}>
                  {t('emailTemplates.previewLive', { defaultValue: 'Live preview — exactly as it will appear in the email body' })}
                </div>
                <iframe
                  title="email-template-preview"
                  sandbox=""
                  srcDoc={`<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>html,body{margin:0;padding:0;width:100%;}body{padding:16px;font-family:Arial,Helvetica,sans-serif;color:#0f172a;background:#fff;box-sizing:border-box;overflow-x:auto;}img,table{max-width:100%;height:auto;}*{box-sizing:border-box;}</style></head><body>${form.body_template || `<p style="color:#94a3b8">${t('emailTemplates.previewEmpty', { defaultValue: '(empty body)' })}</p>`}</body></html>`}
                  style={{ display: 'block', width: '100%', height: 480, border: 'none', background: '#fff' }}
                />
              </div>
            )}
          </div>
          <div className="form-group">
            <label>{t('emailTemplates.activeLabel')}</label>
            <select value={Number(form.is_active) ? '1' : '0'} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.value === '1' ? 1 : 0 }))}>
              <option value="1">{t('common.yes')}</option>
              <option value="0">{t('common.no')}</option>
            </select>
          </div>

          {canManageGlobalTemplates && (
            <div className="form-group">
              <label>{t('emailTemplates.globalTemplateLabel')}</label>
              <select
                value={Number(form.is_global) ? '1' : '0'}
                onChange={(e) => setForm((f) => ({ ...f, is_global: e.target.value === '1' ? 1 : 0 }))}
                title={t('emailTemplates.globalHint')}
              >
                <option value="0">{t('emailTemplates.noPersonal')}</option>
                <option value="1">{t('emailTemplates.yesGlobal')}</option>
              </select>
            </div>
          )}
        </div>

        {editingItem && (
          <div style={{ marginTop: 14 }}>
            <h4 style={{ margin: '8px 0 10px', fontSize: 14, fontWeight: 800 }}>{t('common.history')}</h4>
            {historyLoading ? (
              <div className="loading-spinner"><div className="spinner" /></div>
            ) : (
              <AuditTimeline logs={history} showEntity={false} />
            )}
          </div>
        )}
      </Modal>

      {/* Row-level preview modal — available to everyone, including end users
          who can only browse + select templates from the mailbox compose. */}
      <Modal
        isOpen={!!previewItem}
        onClose={() => setPreviewItem(null)}
        title={previewItem ? t('emailTemplates.previewTitle', { name: previewItem.name, defaultValue: `Preview — ${previewItem.name}` }) : ''}
        size="lg"
        footer={
          <button className="btn btn-outline" onClick={() => setPreviewItem(null)}>{t('common.close', { defaultValue: 'Close' })}</button>
        }
      >
        {previewItem && (
          <div>
            <div style={{
              padding: '10px 14px',
              border: '1px solid var(--border)',
              borderRadius: 8,
              marginBottom: 12,
              background: 'var(--bg-secondary)',
              fontSize: 13
            }}>
              <div style={{ color: 'var(--text-muted)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>
                {t('emailTemplates.subjectTemplateLabel')}
              </div>
              <div style={{ fontWeight: 600 }}>{previewItem.subject_template || '-'}</div>
            </div>
            <div style={{
              border: '1px solid var(--border)',
              borderRadius: 8,
              overflow: 'hidden',
              background: '#fff'
            }}>
              <div style={{
                padding: '8px 12px',
                fontSize: 11,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.4,
                color: 'var(--text-muted)',
                background: 'var(--bg-secondary)',
                borderBottom: '1px solid var(--border)'
              }}>
                {t('emailTemplates.previewBody', { defaultValue: 'Body preview' })}
              </div>
              <iframe
                title="email-template-row-preview"
                sandbox=""
                srcDoc={`<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>html,body{margin:0;padding:0;width:100%;}body{padding:16px;font-family:Arial,Helvetica,sans-serif;color:#0f172a;background:#fff;box-sizing:border-box;overflow-x:auto;}img,table{max-width:100%;height:auto;}*{box-sizing:border-box;}</style></head><body>${previewItem.body_template || `<p style="color:#94a3b8">${t('emailTemplates.previewEmpty', { defaultValue: '(empty body)' })}</p>`}</body></html>`}
                style={{ display: 'block', width: '100%', height: 520, border: 'none', background: '#fff' }}
              />
            </div>
            <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <FiInfo size={13} />
              {t('emailTemplates.previewMergeNote', {
                defaultValue: 'Merge tags like {{student_name}} are shown as-is here; they will be replaced when you select this template in the mailbox.'
              })}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EmailTemplateList;
